<template>
  <div>
    <h1>Create your account</h1>

    <p class="mt-3 font-sans-book text-lg" style="line-height: 1.75rem">
      You’re about to join a community of people who are changing the world. By having a Reimagine account, you can add events to our platform, submit helpful resources to the community resource hub, and create a public profile so community members can find out about you, your offerings, and what you hope to reimagine.
    </p>

    <div class="spacer" />

    <div class="flex flex-col md:flex-row">
      <div class="w-full md:pr-4">
        <label>
          First name <span class="text-red">*</span>
        </label>
        <input
          v-validate="'required'"
          name="first_name"
          type="text"
          class="w-full"
          v-model="form.first_name"
        />
        <div v-show="errors.has('first_name')" class="error-message">
          {{ errors.first('first_name') }}
        </div>
      </div>
      <div class="spacer block md:hidden" />
      <div class="w-full">
        <label>
          Last name <span class="text-red">*</span>
        </label>
        <input
          v-validate="'required'"
          name="last_name"
          type="text"
          class="w-full"
          v-model="form.last_name"
        />
        <div v-show="errors.has('last_name')" class="error-message">
          {{ errors.first('last_name') }}
        </div>
      </div>
    </div>

    <div class="spacer" />

    <div class="w-full">
      <label>
        Email <span class="text-red">*</span>
      </label>
      <input
        name="email"
        v-validate="'required|email'"
        type="email"
        class="w-full"
        v-model="form.email"
      />
      <div v-if="errors.has('email')" class="error-message">
        <template v-if="errors.first('email').includes('been taken')">
          An account already exists for this email address.
          Click here to <router-link :to="{ name: 'user-login'}">log in</router-link>
          or <router-link :to="{ name: 'user-forgot-password'}">reset your password</router-link>.
        </template>
        <template v-else>
          {{ errors.first('email') }}
        </template>
      </div>
    </div>

    <div class="spacer" />

    <div class="w-full">
      <label>
        Password <span class="text-red">*</span>
        <span class="block text-sm font-sans-book text-n-gray-dark mb-5">
          Should contain at least 8 characters, including at least 1 lowercase letter, 1 uppercase letter, and 1 number.
        </span>
      </label>
      <input
        v-validate="'required|min:8|upperCase|number|lowerCase'"
        ref="password"
        name="password"
        type="password"
        class="w-full"
        v-model="form.password"
      />
      <div v-show="errors.has('password')" class="error-message">
        {{ errors.first('password') }}
      </div>
    </div>

    <div class="spacer" />

    <div class="w-full">
      <label>
        Confirm password <span class="text-red">*</span>
      </label>
      <input
        v-validate="'required|confirmed:password'"
        name="password_confirmation"
        type="password"
        class="w-full"
        v-model="form.password_confirmation"
      />
      <div v-show="errors.has('password_confirmation')" class="error-message">
        {{ errors.first('password_confirmation') }}
      </div>
    </div>

    <div class="spacer" />

    <label>
      Terms and conditions
    </label>
    <div class="select-box">
      <label class="w-full text-base font-normal">
        <input name="tos" v-validate="'required'" type="checkbox" v-model="form.tos" />
        <span class="inline-block mr-2 check" />
        <span class="font-normal font-sans-book">
          I agree to Reimagine's <a href="https://letsreimagine.org/terms-conditions/" target="_blank"><span class="sr-only">(opens in a new tab) </span>Terms of Service</a> and <a href="https://letsreimagine.org/privacy-cookies/" target="_blank"><span class="sr-only">(opens in a new tab) </span>Privacy Policy</a>.
        </span>
      </label>
    </div>
    <div v-if="errors.has('tos')" class="block error-message">
      {{ errors.first('tos').replace(' tos ', ' Terms of Service and Privacy agreement ') }}
    </div>

    <div class="spacer" />

    <hr class="w-px bg-n-gray border border-n-gray w-full" />

    <div class="spacer" />

    <div class="flex justify-center">
      <button @click="createAccount" class="button">create my account</button>
    </div>

    <div class="spacer" />
  </div>
</template>

<script>
import { authAPI } from "../../../api/authAPI";
import validation from "../../../mixins/validation";
import {analyticsDataLayer} from "../../../api/analyticsDataLayer";

export default {
  name: "CreateAccount",
  mixins: [ validation ],
  data() {
    return {
      form: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        tos: false
      }
    }
  },
  methods: {
    async createAccount() {
      const hasNoFormErrors = await this.$validator.validateAll();

      if (!hasNoFormErrors) {
        // this.gotoFirstValidationError();
        return;
      }

      const res = await authAPI.register.createAccount(this.form);

      if (res.success) {
        localStorage.setItem("isLoggedIn", "true");
        await this.$store.dispatch("getMe");
        analyticsDataLayer.emit('register_account_created');
        this.$router.push({ name: 'register-create-profile' });

        return;
      }

      Object.keys(res.errors).forEach(formAttr => {
        const errorMessage = res.errors[formAttr].find(() => true);
        this.errors.add({
          field: formAttr,
          msg: errorMessage,
        });
      });
    }
  },
  mounted() {
    analyticsDataLayer.emit('register_start');
  }
}
</script>
